<template>
  <div
    class="input-custom"
    :class="{
      'has-text': !!value,
      invalid: !valid,
    }"
    :data-testid="nameWithoutWhitespace"
  >
    <div class="input__wrapper-custom">
      <label
        v-if="showLabel"
        :class="{ 'display-none': !label ,'required': required === true } "
        class="label will-change"
        :for="nameWithoutWhitespace"
      >
        <slot name="label" v-bind="{ label }">
          {{ label }}
          <span
            class="cursor-pointer"
            :class="{ 'tooltip': tooltip}"
            @mouseover="$emit('toggle-tooltip', true)"
            @mouseleave="$emit('toggle-tooltip', false)"
          >
          </span>
          <span v-if="!required" class="optional"> {{ $t('optional') }} </span>
        </slot>
      </label>
      <label v-if="inputType === 'file'" :for="nameWithoutWhitespace" class="file-label">
        <slot name="label" v-bind="{ label }">
         <span
           class="cursor-pointer file-icon flex middle center"
         >
            {{ $t('Attach files') }}
          </span>
        </slot>
      </label>
      <input
        :id="nameWithoutWhitespace"
        v-focus
        v-bind="$attrs"
        :value="value"
        :required="inputType === 'file' ? false : required"
        :disabled="disabled"
        :multiple="multiple"
        :name="name"
        :checked="checked"
        :class="{ 'input--is-password-custom': isPassword , 'file-attachments': inputType === 'file'}"
        :type="inputType"
        :aria-invalid="!valid"
        :aria-required="required"
        :aria-describedby="
          errorMessage ? `${nameWithoutWhitespace}-error` : null
        "
        v-on="listeners"
        :placeholder="showLabel ? '' : label"
        :tabindex="tabIndex"
      />
      <span class="sf-input__bar-custom"></span>
      <slot
        v-bind="{
          icon,
          isPasswordVisible,
          switchVisibilityPassword,
        }"
        name="icon"
      >
        <SfButton
          class="sf-input__button-custom sf-button--pure"
          :class="{ 'display-none': !icon.icon || isPassword }"
          @click="$emit('click:icon')"
          v-if="!(!icon.icon || isPassword)"
        >
          <SfIcon
            :color="icon.color"
            :size="icon.size"
            :icon="icon.icon"
            class="sf-input__icon"
          >
          </SfIcon>
        </SfButton>
        <SfButton
          :class="{ 'display-none': !isPassword }"
          class="sf-input__password-button"
          type="button"
          aria-label="switch-visibility-password"
          :aria-pressed="isPasswordVisible.toString()"
          @click="switchVisibilityPassword"
          v-if="isPassword"
        >
          <SfIcon
            class="sf-input__password-icon"
            :class="{
              hidden: !isPasswordVisible,
            }"
            icon="show_password"
            size="18px"
            color="black"
          ></SfIcon>
        </SfButton>
      </slot>
    </div>
    <div class="file-attachments-info" v-if="inputType === 'file' && filesUploaded && filesUploaded.length">
      <div class="file-attachments-item" v-for="(file, index) in files">
        <span class="name">{{ file.name }}</span>
        <span class="icon icon-close" @click="clearFiles(file.name)"></span>
      </div>
    </div>
    <div class="sf-input__error-message error" :class="{'__error': !valid, '__hint': !hinted}">
      <transition name="sf-fade">
        <slot name="error-message" v-bind="{ errorMessage }">
          <div
            :id="`${nameWithoutWhitespace}-error`"
            :class="{ 'display-none': valid }"
            aria-live="assertive"
          >
            {{ errorMessage }}
          </div>
        </slot>
      </transition>
      <transition name="sf-fade">
        <slot name="error-message hint-message" v-bind="{ hintMessage }">
          <div
            :id="`${nameWithoutWhitespace}-hint`"
            :class="{ 'display-none': hinted }"
            aria-live="assertive"
          >
            {{ hintMessage }}
          </div>
        </slot>
      </transition>
    </div>
  </div>
</template>
<script>
import SfIcon from "~/node_modules/@storefront-ui/vue/src/components/atoms/SfIcon/SfIcon.vue";
import SfButton from "~/node_modules/@storefront-ui/vue/src/components/atoms/SfButton/SfButton.vue";
import {focus} from "~/node_modules/@storefront-ui/vue/src/utilities/directives";

export default {
  name: "Input",
  directives: {
    focus
  },
  components: {SfIcon, SfButton},
  inheritAttrs: false,
  props: {
    tooltip: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    onLabelClick: {
      type: Function,
      default: () => {
      },
    },
    showLabel: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Number],
      default: "",
    },
    filesUploaded: {},
    label: {
      type: String,
      default: "",
    },
    name: {
      type: String,
      default: "",
    },
    optional: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: "text",
    },
    icon: {
      type: Object,
      default: () => {
        return {icon: ""};
      },
    },
    tabIndex: {
      type: String,
      default: '99',
    },
    valid: {
      type: Boolean,
      default: true,
    },
    hinted: {
      type: Boolean,
      default: true,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    hintMessage: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
      description: "Native input required attribute",
    },
    checked: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      description: "Native input disabled attribute",
    },
    hasShowPassword: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isPasswordVisible: false,
      inputType: "",
      isNumberTypeSafari: false,
      files: this.filesUploaded,
      filesClear: []
    };
  },
  computed: {
    listeners() {
      return {
        ...this.$listeners,
        input: (event) => {
          if(this.inputType === 'file') {
            const files = [];
            for(let f = 0; f < event.target.files.length; f++) {
              files.push(event.target.files[f]);
            }
            this.files = files;
            this.filesClear = [];
          }

          this.$emit("input", (this.inputType !== 'file') ? event.target.value : this.files);
        },
      };
    },
    isPassword() {
      return this.type === "password" && this.hasShowPassword;
    },
    nameWithoutWhitespace() {
      return this.name.replace(/\s/g, "");
    },
  },
  watch: {
    type: {
      immediate: true,
      handler: function (type) {
        let inputType = type;
        // Safari has bug for number input
        if (typeof window !== "undefined" || typeof document !== "undefined") {
          const ua = navigator.userAgent.toLocaleLowerCase();
          if (
            ua.indexOf("safari") !== -1 &&
            ua.indexOf("chrome") === -1 &&
            type === "number"
          ) {
            this.isNumberTypeSafari = true;
            inputType = "text";
          }
        }
        this.inputType = inputType;
      },
    },
    value: {
      immediate: true,
      handler: function (value) {
        if (!this.isNumberTypeSafari) return;
        if (isNaN(value)) {
          this.$emit("input");
        }
      },
    }
  },
  methods: {
    switchVisibilityPassword() {
      if (this.type !== "password") return;
      this.isPasswordVisible = !this.isPasswordVisible;
      this.inputType = this.isPasswordVisible ? "text" : "password";
    },
    clearFiles(name) {
      const dt = new DataTransfer()
      const input = document.getElementById(this.nameWithoutWhitespace)
      const { files } = input

      const filesList = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (file.name !== name) {
          dt.items.add(file)
          filesList.push(file);
        }
      }

      input.files = dt.files // Assign the updates list

      this.$emit("input", filesList);
    },
  },
};
</script>
